import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer } from 'react';

// third-party
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';

// action - state management
import { LOGIN, LOGOUT } from 'store/reducers/actions';
import authReducer from 'store/reducers/auth';

// project import
import Loader from 'components/Loader';

// firebase initialize
if (!firebase.apps.length) {
  firebase.initializeApp({
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
  });
}

// const
const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null
};

// ==============================|| FIREBASE CONTEXT & PROVIDER ||============================== //

const FirebaseContext = createContext(null);

export const FirebaseProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  useEffect(
    () =>
      firebase.auth().onAuthStateChanged((user) => {
        if (user) {
          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              user: {
                id: user.uid,
                email: user.email,
                name: user.displayName || user.email.split('@')[0],
                avatar: user.photoURL,
                role: 'User',
                emailVerified: user.emailVerified
              },
              isEmailVerified: user.emailVerified
            }
          });
        } else {
          dispatch({
            type: LOGOUT
          });
        }
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dispatch]
  );

  const firebaseEmailPasswordSignIn = (email, password) => firebase.auth().signInWithEmailAndPassword(email, password);

  const firebaseGoogleSignIn = () => {
    const provider = new firebase.auth.GoogleAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseTwitterSignIn = () => {
    const provider = new firebase.auth.TwitterAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  const firebaseFacebookSignIn = () => {
    const provider = new firebase.auth.FacebookAuthProvider();
    return firebase.auth().signInWithPopup(provider);
  };

  // const firebaseRegister = async (email, password) => firebase.auth().createUserWithEmailAndPassword(email, password);

  const firebaseRegister = async (email, password, additionalData) => {
    const userCredential = await firebase.auth().createUserWithEmailAndPassword(email, password);
    const user = userCredential.user;
    console.log('register triggered for user:', user);
    // Send additional data to your backend
    const token = await user.getIdToken();
    try {
      // Read experiment cookie set at HTML level (e.g., "AI_chat_exp_01_A" or "AI_chat_exp_01_B")
      const expMatch = typeof document !== 'undefined' && (document.cookie || '').match(/(?:^|; )exp=([^;]+)/);
      const experimentVariant = expMatch && expMatch[1];
      await axios.post(
        `${config.apiUrl}/pro_plans/get_additional_data_on_register`,
        {
          ...additionalData,
          uid: user.uid,
          variant: experimentVariant
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          xsrfCookieName: 'XSRF-TOKEN',
          xsrfHeaderName: 'X-XSRF-TOKEN'
        }
      );
    } catch (error) {
      console.error('Error sending additional data to backend:', error);
      // You might want to handle this error, perhaps by deleting the created Firebase user
    }

    return userCredential;
  };

  const logout = () => firebase.auth().signOut();

  const resetPassword = async (email) => {
    await firebase.auth().sendPasswordResetEmail(email);
  };

  const updateProfile = () => {};
  if (state.isInitialized !== undefined && !state.isInitialized) {
    return <Loader />;
  }

  const setNewPassword = async (oldPassword, newPassword) => {
    if (state.isLoggedIn) {
      try {
        const user = firebase.auth().currentUser;
        const credential = firebase.auth.EmailAuthProvider.credential(user.email, oldPassword);
        await user.reauthenticateWithCredential(credential);
        await user.updatePassword(newPassword);
        console.log('Password updated successfully.');
      } catch (error) {
        console.error('Error updating password:', error);
      }
    } else {
      console.log('User is not logged in.');
    }
  };

  const verifyPasswordResetCode = (code) => {
    return firebase.auth().verifyPasswordResetCode(code);
  };

  const applyActionCode = (code) => {
    return firebase.auth().applyActionCode(code);
  };

  const confirmPasswordReset = (code, newPassword) => {
    return firebase.auth().confirmPasswordReset(code, newPassword);
  };

  const verifyEmail = (code) => {
    return firebase.auth().applyActionCode(code);
  };

  return (
    <FirebaseContext.Provider
      value={{
        ...state,
        firebaseRegister,
        firebaseEmailPasswordSignIn,
        login: () => {},
        firebaseGoogleSignIn,
        firebaseTwitterSignIn,
        firebaseFacebookSignIn,
        logout,
        resetPassword,
        updateProfile,
        setNewPassword,
        verifyPasswordResetCode,
        applyActionCode,
        confirmPasswordReset,
        verifyEmail
      }}
    >
      {children}
    </FirebaseContext.Provider>
  );
};

FirebaseProvider.propTypes = {
  children: PropTypes.node
};

export default FirebaseContext;
